/* ASAKA — product page behaviour. No dependencies. Loaded with `defer` by every asaka-* section (guarded). */
(() => {
  if (window.__asakaUI) return;
  window.__asakaUI = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollBehavior = reduceMotion ? 'auto' : 'smooth';

  /* ---------- Reveal on scroll (only for [data-ak-reveal], used below the fold) ---------- */
  const revealIO = 'IntersectionObserver' in window && !reduceMotion
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          revealIO.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px' })
    : null;

  const observeReveal = (scope) => {
    scope.querySelectorAll('[data-ak-reveal]:not(.is-in)').forEach((el) => {
      if (revealIO) revealIO.observe(el); else el.classList.add('is-in');
    });
  };
  if (revealIO) document.documentElement.classList.add('ak-js');
  observeReveal(document);
  document.addEventListener('shopify:section:load', (event) => observeReveal(event.target));

  /* ---------- Product ---------- */
  class AkProduct extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.form = this.querySelector('form[action*="/cart/add"]');
      this.idInput = this.form && this.form.querySelector('[name="id"]');
      this.atc = this.querySelector('[data-atc]');
      try {
        this.variants = JSON.parse(this.querySelector('[data-ak-variants]').textContent);
      } catch (e) {
        this.variants = [];
      }
      this.initGallery();
      this.initVariants();
      this.initQuantity();
      this.initSticky();
      this.initErrors();
    }

    disconnectedCallback() {
      this.abort.abort();
      if (this.galleryIO) this.galleryIO.disconnect();
      if (this.stickyIO) this.stickyIO.disconnect();
    }

    initGallery() {
      const track = this.querySelector('[data-track]');
      if (!track) return;
      this.track = track;
      this.slides = Array.from(track.children);
      const thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      const dots = Array.from(this.querySelectorAll('[data-dot]'));
      const counter = this.querySelector('[data-current]');

      const thumbRow = this.querySelector('[data-thumbs]');
      this.current = 0;
      const setActive = this.setActive = (index) => {
        this.current = index;
        thumbs.forEach((t, i) => t.setAttribute('aria-current', String(i === index)));
        dots.forEach((d, i) => d.setAttribute('aria-current', String(i === index)));
        if (counter) counter.textContent = index + 1;
        const active = thumbs[index];
        if (thumbRow && active && thumbRow.scrollWidth > thumbRow.clientWidth) {
          const left = active.offsetLeft - (thumbRow.clientWidth - active.offsetWidth) / 2;
          thumbRow.scrollTo({ left, behavior: scrollBehavior });
        }
      };

      this.galleryIO = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(this.slides.indexOf(entry.target));
        });
      }, { root: track, threshold: 0.6 });
      this.slides.forEach((slide) => this.galleryIO.observe(slide));

      thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.goTo(i), { signal: this.abort.signal }));
    }

    goTo(index) {
      if (!this.track || !this.slides[index]) return;
      if (this.setActive) this.setActive(index);  /* update immediately; the observer confirms after scrolling */
      this.track.scrollTo({ left: index * this.track.clientWidth, behavior: scrollBehavior });
    }

    initVariants() {
      const picker = this.querySelector('[data-ak-picker]');
      if (!picker) return;
      picker.addEventListener('change', () => {
        const fieldsets = Array.from(picker.querySelectorAll('fieldset'));
        const selected = fieldsets.map((fs) => {
          const input = fs.querySelector('input:checked');
          const label = fs.querySelector('[data-ak-selected]');
          if (label && input) label.textContent = input.value;
          return input ? input.value : null;
        });
        const variant = this.variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
        this.updateVariant(variant);
      }, { signal: this.abort.signal });
    }

    updateVariant(variant) {
      const label = this.atc && this.atc.querySelector('[data-atc-label]');
      if (!variant) {
        if (this.atc) this.atc.disabled = true;
        if (label) label.textContent = 'Unavailable';
        return;
      }
      if (this.idInput) {
        this.idInput.value = variant.id;
        this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.querySelectorAll('[data-ak-price]').forEach((el) => { el.innerHTML = variant.price_html; });
      const stickyPrice = this.querySelector('[data-ak-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = variant.price;
      if (this.atc) {
        this.atc.disabled = !variant.available;
        if (label) label.textContent = variant.available ? (this.atc.dataset.label || label.textContent) : 'Sold out';
      }
      const stickyBtn = this.querySelector('[data-sticky-btn]');
      if (stickyBtn) stickyBtn.disabled = !variant.available;

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      if (variant.media && this.slides) {
        const index = this.slides.findIndex((s) => s.dataset.mediaId === String(variant.media));
        if (index > -1) this.goTo(index);
      }
    }

    initQuantity() {
      const input = this.querySelector('.ak-qty input');
      if (!input) return;
      this.querySelectorAll('[data-qty]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const next = Math.max(1, (parseInt(input.value, 10) || 1) + parseInt(btn.dataset.qty, 10));
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }, { signal: this.abort.signal });
      });
    }

    initSticky() {
      const bar = this.querySelector('[data-sticky]');
      if (!bar || !this.atc) return;
      const stickyBtn = bar.querySelector('[data-sticky-btn]');

      this.stickyIO = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', show);
        bar.inert = !show;
      });
      this.stickyIO.observe(this.atc);

      stickyBtn.addEventListener('click', () => {
        /* If a bundle picker (e.g. Kaching Bundles) is on the page, bring the shopper back to it
           so they choose their bundle. Otherwise add the current variant straight to the basket. */
        const bundlePicker = this.querySelector('.ak-app [class*="kaching" i], .ak-app > *, [class*="kaching-bundle" i]');
        if (bundlePicker) {
          bundlePicker.scrollIntoView({ behavior: scrollBehavior, block: 'center' });
        } else {
          this.atc.click();
        }
      }, { signal: this.abort.signal });
    }

    initErrors() {
      if (!this.form) return;
      const box = this.querySelector('[data-ak-error]');
      if (!box) return;
      this.form.addEventListener('cart:error', (event) => {
        box.textContent = (event.detail && event.detail.error) || 'Something went wrong. Please try again.';
      }, { signal: this.abort.signal });
      this.form.addEventListener('variant:add', () => { box.textContent = ''; }, { signal: this.abort.signal });
    }
  }


  /* ---------- Rail: swipe track with a hairline progress bar (mobile/tablet) ---------- */
  class AkRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-rail-track]');
      this.bar = this.querySelector('[data-rail-bar]');
      if (!this.track || !this.bar) return;
      this.abort = new AbortController();
      let ticking = false;
      const update = () => {
        ticking = false;
        const { scrollLeft, scrollWidth, clientWidth } = this.track;
        const ratio = scrollWidth > clientWidth ? (scrollLeft + clientWidth) / scrollWidth : 1;
        this.bar.style.width = `${Math.min(100, ratio * 100)}%`;
        if (this.syncArrows) this.syncArrows();
      };
      const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
      this.track.addEventListener('scroll', onScroll, { passive: true, signal: this.abort.signal });
      const section = this.closest('section') || this;
      const prev = section.querySelector('[data-rail-prev]');
      const next = section.querySelector('[data-rail-next]');
      const step = (dir) => {
        const card = this.track.firstElementChild;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const amount = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth * 0.8;
        this.track.scrollBy({ left: dir * amount, behavior: scrollBehavior });
      };
      if (prev) prev.addEventListener('click', () => step(-1), { signal: this.abort.signal });
      if (next) next.addEventListener('click', () => step(1), { signal: this.abort.signal });
      this.syncArrows = () => {
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        if (prev) prev.disabled = this.track.scrollLeft <= 2;
        if (next) next.disabled = this.track.scrollLeft >= max;
      };
      window.addEventListener('resize', onScroll, { passive: true, signal: this.abort.signal });
      update();
    }
    disconnectedCallback() { if (this.abort) this.abort.abort(); }
  }


  /* ---------- Sale banner: label from the real end date (client clock), hide when over ---------- */
  const syncSales = (scope) => {
    scope.querySelectorAll('[data-ak-sale]').forEach((el) => {
      const end = el.dataset.end;
      if (!end || !/^\d{4}-\d{2}-\d{2}$/.test(end)) return;
      const [y, m, d] = end.split('-').map(Number);
      const endDay = new Date(y, m - 1, d);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const days = Math.round((endDay - today) / 86400000);
      const label = el.querySelector('[data-ak-sale-label]');
      if (days < 0) { el.hidden = true; return; }
      if (label) {
        if (days === 0) label.textContent = 'Ends today';
        else if (days === 1) label.textContent = 'Ends tomorrow';
        else if (days <= 7) label.textContent = `Ends in ${days} days`;
      }
    });
  };
  syncSales(document);
  document.addEventListener('shopify:section:load', (event) => syncSales(event.target));


  /* ---------- Videos: play only while visible (saves data/battery), one unmuted at a time ---------- */
  const videoIO = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const v = entry.target;
          if (entry.isIntersecting && !reduceMotion) { v.play().catch(() => {}); } else { v.pause(); }
        });
      }, { threshold: 0.35 })
    : null;
  const initVideos = (scope) => {
    scope.querySelectorAll('video[data-ak-autoplay]').forEach((v) => {
      if (v.dataset.akBound) return;
      v.dataset.akBound = '1';
      if (videoIO) videoIO.observe(v);
    });
  };
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-ak-sound]');
    if (!btn) return;
    const video = btn.parentElement.querySelector('video');
    if (!video) return;
    const turnOn = video.muted;
    document.querySelectorAll('[data-ak-sound][aria-pressed="true"]').forEach((other) => {
      if (other === btn) return;
      const ov = other.parentElement.querySelector('video');
      if (ov) ov.muted = true;
      other.setAttribute('aria-pressed', 'false');
      other.setAttribute('aria-label', 'Turn sound on');
    });
    video.muted = !turnOn;
    if (turnOn) video.play().catch(() => {});
    btn.setAttribute('aria-pressed', String(turnOn));
    btn.setAttribute('aria-label', turnOn ? 'Turn sound off' : 'Turn sound on');
  });
  initVideos(document);
  document.addEventListener('shopify:section:load', (event) => initVideos(event.target));


  /* ---------- "Ships by" date: today in the UK + N days (pages can be served from cache) ---------- */
  const ukParts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: 'numeric', day: 'numeric' });
  const shortDate = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' });
  const syncShipDates = (scope) => {
    scope.querySelectorAll('[data-ak-ship]').forEach((el) => {
      const days = parseInt(el.dataset.days, 10) || 1;
      const p = ukParts.formatToParts(new Date());
      const g = (t) => parseInt(p.find((x) => x.type === t).value, 10);
      const out = el.querySelector('[data-ak-ship-date]');
      if (out) out.textContent = shortDate.format(new Date(Date.UTC(g('year'), g('month') - 1, g('day') + days, 12))).replace(',', '');
    });
  };
  syncShipDates(document);
  document.addEventListener('shopify:section:load', (event) => syncShipDates(event.target));


  /* ---------- Video lightbox: tap a thumbnail -> play large with sound ---------- */
  document.addEventListener('click', (event) => {
    const open = event.target.closest('[data-ak-vopen]');
    if (open) {
      const scope = open.closest('.ak-vids') || open.closest('section');
      const modal = scope && scope.querySelector('[data-ak-vmodal]');
      if (!modal || typeof modal.showModal !== 'function') return;
      const video = modal.querySelector('video');
      video.src = open.dataset.src;
      video.poster = open.dataset.poster || '';
      video.muted = false;
      modal.showModal();
      video.play().catch(() => { video.muted = true; video.play().catch(() => {}); });
      return;
    }
    const modal = event.target.closest('[data-ak-vmodal]');
    if (modal && (event.target.closest('[data-ak-vclose]') || event.target === modal)) modal.close();
  });
  document.addEventListener('close', (event) => {
    if (!event.target.matches || !event.target.matches('[data-ak-vmodal]')) return;
    const video = event.target.querySelector('video');
    video.pause();
    video.removeAttribute('src');
    video.load();
  }, true);


  /* ---------- Lab test popup: open / close / paginate ---------- */
  document.addEventListener('click', (event) => {
    const openBtn = event.target.closest('[data-ak-lab-open]');
    if (openBtn) {
      const dlg = openBtn.closest('.ak-lab').querySelector('[data-ak-lab-dialog]');
      if (dlg) dlg.showModal();
      return;
    }
    const dlg = event.target.closest('[data-ak-lab-dialog]');
    if (!dlg) return;
    if (event.target.closest('[data-ak-lab-close]')) { dlg.close(); return; }
    if (event.target === dlg) { dlg.close(); return; }

    const prev = event.target.closest('[data-ak-lab-prev]');
    const next = event.target.closest('[data-ak-lab-next]');
    if (!prev && !next) return;
    const pages = dlg.querySelectorAll('[data-ak-lab-page]');
    let cur = 0;
    pages.forEach((p, i) => { if (!p.hidden) cur = i; });
    const tgt = prev ? cur - 1 : cur + 1;
    if (tgt < 0 || tgt >= pages.length) return;
    pages[cur].hidden = true;
    pages[tgt].hidden = false;
    const ct = dlg.querySelector('[data-ak-lab-count]');
    if (ct) ct.textContent = (tgt + 1) + ' of ' + pages.length;
    const pBtn = dlg.querySelector('[data-ak-lab-prev]');
    const nBtn = dlg.querySelector('[data-ak-lab-next]');
    if (pBtn) pBtn.disabled = tgt === 0;
    if (nBtn) nBtn.disabled = tgt === pages.length - 1;
  });

  /* ---------- Bundles: show the choice the moment the finger lands; Kaching confirms it a moment later ---------- */
  (() => {
    let pending = null;
    let timer = 0;
    const clear = () => {
      clearTimeout(timer);
      if (pending) pending.classList.remove('ak-pending');
      pending = null;
    };
    document.addEventListener('pointerdown', (event) => {
      const bar = event.target.closest && event.target.closest('.kaching-bundles__bars > .kaching-bundles__bar');
      if (!bar || bar.classList.contains('kaching-bundles__bar--selected') || event.target.closest('select, input, a')) return;
      clear();
      pending = bar;
      bar.classList.add('ak-pending');
      timer = setTimeout(clear, 1200);
    }, { passive: true });
    document.addEventListener('pointercancel', clear, { passive: true });
  })();

  if (!window.customElements.get('ak-product')) window.customElements.define('ak-product', AkProduct);
  if (!window.customElements.get('ak-rail')) window.customElements.define('ak-rail', AkRail);

  /* Countdown timers (stock bar, announcement bar) — tick to midnight UK (Europe/London) */
  (function initTimer() {
    const els = document.querySelectorAll('[data-ak-timer]');
    if (!els.length) return;
    var ukFmt = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London', hour: 'numeric', minute: 'numeric', second: 'numeric', hour12: false
    });
    function tick() {
      var parts = ukFmt.formatToParts(new Date());
      var g = function(t) { var p = parts.find(function(p) { return p.type === t; }); return parseInt(p.value, 10); };
      var left = Math.max(0, 86400 - (g('hour') * 3600 + g('minute') * 60 + g('second')));
      var text =
        String(Math.floor(left / 3600)).padStart(2, '0') + ':' +
        String(Math.floor((left % 3600) / 60)).padStart(2, '0') + ':' +
        String(left % 60).padStart(2, '0');
      els.forEach(function (el) { if (el.textContent !== text) el.textContent = text; });
    }
    tick();
    setInterval(tick, 1000);
  })();
})();

